import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faAddressCard,
  faGraduationCap,
  faVideo,
  faFloppyDisk,
  faCircleCheck,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import './EditStudentInfo.css';

const API = process.env.REACT_APP_API_BASE_URL;

/* Demo student: same fields, same JSON body, same endpoint as before. */
const EditStudentInfodemo = ({ student, setStudent }) => {
  const [formData, setFormData] = useState({
    firstName: student.FirstName || '',
    lastName: student.LastName || '',
    email: student.ParentsEmail || '',
    mobileNumber: student.MobileNumber || '',
    birthday: student.Birthday ? String(student.Birthday).split('T')[0] : '',
    age: student.Age || '',
    grade: student.Grade || '',
    IdGradesPlans: student.IdGradesPlans || 1,
    CodeJoinDemo: student.CodeJoinDemo || '',
    ZoomLink: student.ZoomLink || '',
    Type: student.Type === 0 ? 'Paid' : 'Demo',
    Offset: student.Offset,
  });
  const [OffsetStudent, setOffsetStudent] = useState(student.Offset);
  const [OffsetStudentList, setOffsetStudentList] = useState([]);
  const [listError, setListError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const studentId = student?.IdUser;

  // ---------- Time zones (same endpoint), once per student ----------
  useEffect(() => {
    if (!studentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/studentprofile/OffsetList`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
        });
        if (!cancelled) setOffsetStudentList(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching timezones:', err);
        if (!cancelled) setListError('Couldn’t load the time zones.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    setSuccess(false);
  };

  // ---------- Save (same endpoint + same body) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const dataToSend = { ...formData, Offset: OffsetStudent, birthday: formData.birthday || null };
      const res = await axios.put(`${API}/api/admin/studentprofile/updatedemo/${student.IdUser}`, dataToSend, {
        headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` },
      });
      const saved = res.data;
      if (saved && typeof saved === 'object' && 'FirstName' in saved) {
        setStudent(saved);
      } else {
        setStudent((s) => ({
          ...s,
          FirstName: formData.firstName,
          LastName: formData.lastName,
          ParentsEmail: formData.email,
          MobileNumber: formData.mobileNumber,
          Birthday: formData.birthday || null,
          Age: formData.age,
          Grade: formData.grade,
          IdGradesPlans: Number(formData.IdGradesPlans) || formData.IdGradesPlans,
          CodeJoinDemo: formData.CodeJoinDemo,
          ZoomLink: formData.ZoomLink,
          Offset: OffsetStudent,
        }));
      }
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update student');
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, type = 'text', extra = {}) => (
    <label className="rmaes-field">
      <span>
        {label}
        {extra.required && <b aria-hidden="true"> *</b>}
      </span>
      <input type={type} name={name} value={formData[name] ?? ''} onChange={handleChange} disabled={loading} {...extra} />
    </label>
  );

  return (
    <div className="rmaes">
      <form onSubmit={handleSubmit}>
        <div className="rmaes-grid">
          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faUser} /> Student
            </h3>
            <div className="rmaes-row">
              {field('firstName', 'First name', 'text', { required: true, autoComplete: 'off' })}
              {field('lastName', 'Last name', 'text', { required: true, autoComplete: 'off' })}
            </div>
            <div className="rmaes-row rmaes-row--3">
              {field('birthday', 'Birthday', 'date')}
              {field('age', 'Age', 'number', { min: 3, max: 99 })}
              {field('grade', 'Grade')}
            </div>
          </section>

          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faAddressCard} /> Contact
            </h3>
            {field('email', 'Parent email', 'email', { required: true, autoComplete: 'off' })}
            {field('mobileNumber', 'Mobile number', 'tel', { required: true, autoComplete: 'off' })}
          </section>

          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faGraduationCap} /> Demo
            </h3>
            <div className="rmaes-row">
              <label className="rmaes-field">
                <span>Grade plan</span>
                <select name="IdGradesPlans" value={formData.IdGradesPlans} onChange={handleChange} disabled={loading}>
                  <option value="1">Little</option>
                  <option value="2">Junior</option>
                </select>
              </label>
              {field('CodeJoinDemo', 'Demo join code', 'text', { required: true, autoComplete: 'off' })}
            </div>
          </section>

          <section className="rmaes-card">
            <h3>
              <FontAwesomeIcon icon={faVideo} /> Class
            </h3>
            {field('ZoomLink', 'Zoom link', 'text', { placeholder: 'https://zoom.us/j/…' })}
            <label className="rmaes-field">
              <span>Time zone</span>
              <select value={OffsetStudent ?? ''} onChange={(e) => setOffsetStudent(e.target.value)} disabled={loading}>
                <option value="">Select time zone</option>
                {OffsetStudentList.map((o) => (
                  <option key={o.Id} value={o.Id}>
                    {o.Timezone} ({o.TimeHours >= 0 ? '+' : ''}
                    {o.TimeHours})
                  </option>
                ))}
              </select>
              {listError && <small className="is-error">{listError}</small>}
            </label>
          </section>
        </div>

        <div className="rmaes-bar" aria-live="polite">
          <p className={error ? 'is-error' : success ? 'is-ok' : ''}>
            {error ? (
              <>
                <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
              </>
            ) : success ? (
              <>
                <FontAwesomeIcon icon={faCircleCheck} /> Student information updated.
              </>
            ) : (
              'Demo student · changes are saved together.'
            )}
          </p>
          <button type="submit" className="rmaes-btn" disabled={loading}>
            <FontAwesomeIcon icon={faFloppyDisk} /> {loading ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditStudentInfodemo;